/**
 * Domestic wizard state — ported from V2's store/domesticWizardSlice.ts.
 *
 * IMPORTANT: This slice is NOT persisted. It holds sensitive LE data (target
 * identifiers, case references, agent info) which must not survive page refresh / tab
 * close / next session. State is in-memory only; resetWizard() clears it on Cancel. Do
 * NOT add this slice to a persistReducer config.
 *
 * The V2 two-step submit (upload files, then post form) is kept as two calls, but in
 * the reverse order: submitRequestThunk maps the wizard state onto CMS's contract (see
 * cmsMapping.ts) and files the case through the cases API service (see api/casesApi.ts),
 * then uploads the collected files against the returned case id — CMS keeps attachments
 * on a separate surface from the case (A1).
 *
 * The API service is an RTK Query slice, but this workflow drives it imperatively and
 * resets each mutation entry the instant it has read the result, so the sensitive case
 * payload never lingers in RTK Query's cache — the in-memory-only rule above holds for
 * the transport too.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  TypeOfRequestForm,
  NatureOfCrimeForm,
  NDOForm,
  AgentInformationForm,
  TypeOfServiceForm,
  FileUploadForm,
  DomesticReviewForm,
  UploadedFile,
} from './types';
import type { FailedAttachment, SubmittedCase } from '../api/contracts';
import { casesApi } from '../api/casesApi';
import { isApiFailure } from '../api/errors';
import { buildDomesticCaseForm } from './cmsMapping';

/**
 * What one submit produced.
 *
 * A filed case and a list of files that did not attach are separate facts: the first
 * says the submit succeeded, the second says part of it needs following up by hand. A
 * single success/failure value could not carry both.
 */
export interface SubmitOutcome {
  filed: SubmittedCase;
  failedAttachments: FailedAttachment[];
}

/** Complete domestic wizard state. */
export interface DomesticWizardState {
  currentStep: number;
  totalSteps: number;

  typeOfRequest: TypeOfRequestForm;
  natureOfCrime: NatureOfCrimeForm;
  ndo: NDOForm;
  agentInfo: AgentInformationForm;
  typeOfService: TypeOfServiceForm;
  fileUpload: FileUploadForm;
  review: DomesticReviewForm;

  submittedCaseId: string | null;

  /**
   * Files that did not attach to an otherwise successfully filed case.
   *
   * Kept separately from `error` because the two call for opposite responses: an error
   * means try again, this means do not — the case exists and only these files are
   * missing.
   */
  failedAttachments: FailedAttachment[];

  isSubmitting: boolean;
  submitSuccess: boolean;
  error: string | null;

  validationErrors: Record<string, string>;
}

const initialTypeOfRequest: TypeOfRequestForm = {
  cpConcern: null,
  isPreservationRequest: null,
  title: undefined,
  leReferenceNumber: '',
  isWAShieldLaw: null,
  waShieldLawAcknowledgement: '',
  hasDifferentRecipient: false,
  deliveryEmail: undefined,
  notifyTarget: null,
  requestType: null,
  requestSubType: null,
  isDSAOrder: null,
  legalOrderType: null,
};

const initialNatureOfCrime: NatureOfCrimeForm = {
  isTerrorism: false,
  isChildExploitation: false,
  isHumanTrafficking: false,
  isMissingPerson: false,
  isCorruption: false,
  isCybercrime: false,
  isDefamation: false,
  isHateSpeech: false,
  isMoneyLaundering: false,
  isHarassment: false,
  isTheft: false,
  isViolentCrime: false,
  isAdultSexualAbuseOrExploitation: false,
  isDrugsOrDrugTrafficking: false,
  isFraudWithMoneyLaundering: false,
  isFraudNoMoneyLaundering: false,
  isOther: false,
  isOtherDescription: undefined,
};

const initialNDO: NDOForm = {
  hasNdo: null,
  isNdoConfirm: undefined,
  listOfNumbersToExtend: undefined,
  isNdoUploadFinalConfirm: undefined,
};

const initialAgentInfo: AgentInformationForm = {
  agencyName: '',
  agentFirstName: '',
  agentLastName: '',
  agentEmail: '',
  agentPhoneNumber: '',
  agentPhoneCountryCode: '+1',
  hasReceivingAgent: false,
  receivingAgentFirstName: '',
  receivingAgentLastName: '',
  receivingAgentEmail: '',
  receivingAgentPhone: '',
  receivingAgentPhoneCountryCode: '+1',
  receivingAgentPhoneSameAsSubmitting: false,
};

const initialTypeOfService: TypeOfServiceForm = {
  selectedServiceIds: [],
  manualServices: [],
  identifiers: [],
  dataCategories: [],
  otherDataCategories: [],
  requestStartDate: null,
  requestEndDate: null,
};

const initialFileUpload: FileUploadForm = {
  files: [],
};

const initialReview: DomesticReviewForm = {
  acknowledgement: '',
  perjuryAcknowledgement: '',
};

const initialState: DomesticWizardState = {
  currentStep: 0,
  totalSteps: 7,
  typeOfRequest: initialTypeOfRequest,
  natureOfCrime: initialNatureOfCrime,
  ndo: initialNDO,
  agentInfo: initialAgentInfo,
  typeOfService: initialTypeOfService,
  fileUpload: initialFileUpload,
  review: initialReview,
  submittedCaseId: null,
  failedAttachments: [],
  isSubmitting: false,
  submitSuccess: false,
  error: null,
  validationErrors: {},
};

/**
 * Submits the request to CMS.
 *
 * Files the case first, then attaches the collected files against the returned case id.
 * The idempotency key and retry policy for the case live inside the cases API service,
 * not here; attachments are sent once, without a key. An attachment failure surfaces the
 * case id so a filed-but-unattached case is not mistaken for a failed submit and re-filed.
 *
 * Each mutation entry is reset once its result is in hand: the cases API service holds no
 * sensitive payload after the workflow returns.
 */
export const submitRequestThunk = createAsyncThunk<
  SubmitOutcome,
  void,
  { state: { domesticWizard: DomesticWizardState }; rejectValue: string }
>('domesticWizard/submitRequest', async (_, { getState, dispatch, rejectWithValue }) => {
  const state = getState().domesticWizard;
  const submit = dispatch(
    casesApi.endpoints.submitDomesticCase.initiate(buildDomesticCaseForm(state)),
  );

  try {
    const filed = await submit.unwrap();

    const files = state.fileUpload.files
      .map((tracked) => tracked.file)
      .filter((file): file is File => file instanceof File);

    const upload = dispatch(
      casesApi.endpoints.uploadCaseAttachments.initiate({ caseId: filed.caseId, files }),
    );

    try {
      const batch = await upload.unwrap();
      return { filed, failedAttachments: batch.failed };
    } finally {
      upload.reset();
    }
  } catch (error) {
    // The message is already the portal's own, chosen from the error code. Nothing the
    // server wrote as prose is displayed.
    const message = isApiFailure(error)
      ? error.message
      : error instanceof Error
        ? error.message
        : 'Failed to submit request';
    return rejectWithValue(message);
  } finally {
    submit.reset();
  }
});

export const domesticWizardSlice = createSlice({
  name: 'domesticWizard',
  initialState,
  reducers: {
    setStep: (state, action: PayloadAction<number>) => {
      state.currentStep = Math.max(0, Math.min(action.payload, state.totalSteps - 1));
    },
    nextStep: (state) => {
      if (state.currentStep < state.totalSteps - 1) {
        state.currentStep += 1;
      }
    },
    prevStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },

    updateTypeOfRequest: (state, action: PayloadAction<Partial<TypeOfRequestForm>>) => {
      state.typeOfRequest = { ...state.typeOfRequest, ...action.payload };
      state.validationErrors = {};
      state.error = null;
    },

    updateNatureOfCrime: (state, action: PayloadAction<Partial<NatureOfCrimeForm>>) => {
      state.natureOfCrime = { ...state.natureOfCrime, ...action.payload };
      state.validationErrors = {};
      state.error = null;
    },

    updateNdo: (state, action: PayloadAction<Partial<NDOForm>>) => {
      state.ndo = { ...state.ndo, ...action.payload };
      state.validationErrors = {};
      state.error = null;
    },

    updateAgentInfo: (state, action: PayloadAction<Partial<AgentInformationForm>>) => {
      state.agentInfo = { ...state.agentInfo, ...action.payload };
      state.validationErrors = {};
      state.error = null;
    },

    updateTypeOfService: (state, action: PayloadAction<Partial<TypeOfServiceForm>>) => {
      state.typeOfService = { ...state.typeOfService, ...action.payload };
      state.validationErrors = {};
      state.error = null;
    },

    addFile: (state, action: PayloadAction<UploadedFile>) => {
      state.fileUpload.files.push(action.payload);
      state.validationErrors = {};
      state.error = null;
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.fileUpload.files = state.fileUpload.files.filter((f) => f.id !== action.payload);
      state.validationErrors = {};
    },

    updateReview: (state, action: PayloadAction<Partial<DomesticReviewForm>>) => {
      state.review = { ...state.review, ...action.payload };
      state.validationErrors = {};
      state.error = null;
    },

    setValidationErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = {};
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },

    resetWizard: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitRequestThunk.pending, (state) => {
        state.isSubmitting = true;
        state.submitSuccess = false;
        state.error = null;
      })
      .addCase(submitRequestThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.submitSuccess = true;
        state.submittedCaseId = action.payload.filed.caseId;
        state.failedAttachments = action.payload.failedAttachments;
      })
      .addCase(submitRequestThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitSuccess = false;
        state.error = action.payload ?? 'Submission failed';
      });
  },
});

export const {
  setStep,
  nextStep,
  prevStep,
  updateTypeOfRequest,
  updateNatureOfCrime,
  updateNdo,
  updateAgentInfo,
  updateTypeOfService,
  addFile,
  removeFile,
  updateReview,
  setValidationErrors,
  clearValidationErrors,
  setError,
  clearError,
  resetWizard,
} = domesticWizardSlice.actions;

export const selectDomesticWizard = (state: { domesticWizard: DomesticWizardState }) =>
  state.domesticWizard;
export const selectIsSubmitting = (state: { domesticWizard: DomesticWizardState }) =>
  state.domesticWizard.isSubmitting;
export const selectSubmitSuccess = (state: { domesticWizard: DomesticWizardState }) =>
  state.domesticWizard.submitSuccess;
export const selectValidationErrors = (state: { domesticWizard: DomesticWizardState }) =>
  state.domesticWizard.validationErrors;

export default domesticWizardSlice.reducer;
